import { Box, Button, SxProps, Typography, styled } from "@outschool/backpack";
import {
  Enrollment as EnrollmentBackendType,
  Section as SectionBackendType
} from "@outschool/gql-backend-generated";
import { ParentUpcomingClassesQueryQuery } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { leaderPath } from "@outschool/routes";
import { useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile, useNavigation } from "@outschool/ui-utils";
import React from "react";

import { transcriptsPath } from "../../../shared/Routes";
import { useParentUpcomingClassesQuery } from "../../hooks/useParentUpcomingClassesQuery";
import useRecentlyCompletedClassList from "../../hooks/useRecentlyCompletedClassList";
import { useActiveLearner } from "../../providers/ActiveLearnerProvider";
import Link from "../Link";
import ClassList, { EmptyListView } from "./ClassList";
import ClassListTabs from "./ClassListTabs";
import NextMeetingsOfParent from "./NextMeetingsOfParent";

export type SectionType =
  // @ts-ignore TS(2339): Property 'activeEnrollments' does not exist on typ... Remove this comment to see the full error message
  ParentUpcomingClassesQueryQuery["currentUser"]["activeEnrollments"][0]["section"];
export type LeaderType = SectionType["leader"];
export type LearnerType =
  // @ts-ignore TS(2339): Property 'activeEnrollments' does not exist on typ... Remove this comment to see the full error message
  ParentUpcomingClassesQueryQuery["currentUser"]["activeEnrollments"][0]["learner"];
export type EnrollmentType = EnrollmentBackendType & {
  section: SectionBackendType;
};
export const ClassImage = styled("img")({
  borderRadius: "8px",
  objectFit: "cover"
});

export const Seperator = () => (
  <Box
    sx={{
      color: "grey.200",
      marginX: 8
    }}
  >
    •
  </Box>
);

export interface ClassDescriptionProps {
  enrollment: EnrollmentType;
  learner: LearnerType;
  section: SectionType;
}

interface ClassTeacherProps {
  leader: LeaderType;
  isLinkComponent: boolean;
}

enum ClassListTab {
  Upcoming = "upcoming",
  Completed = "completed"
}

export const ClassTeacher = ({
  leader,
  isLinkComponent = true
}: ClassTeacherProps) => (
  <Box
    component={isLinkComponent ? Link : "div"}
    target="_blank"
    to={
      isLinkComponent ? leaderPath(leader.uid, leader.leader_link) : undefined
    }
    onClick={(event: React.MouseEvent<HTMLElement>) => {
      // Stop link click from bubbling to link underneath
      event.stopPropagation();
    }}
    aria-label={leader?.name}
    flex
    sx={{
      alignItems: "center",
      gap: 8
    }}
  >
    <UserHeadshotImage user={leader} size={32} />
    <Typography
      variant="body2"
      sx={{
        color: "grey.600",
        width: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      }}
    >
      {leader.name}
    </Typography>
  </Box>
);

export interface ClassProps {
  enrollment: EnrollmentType;
  sx?: SxProps;
}

export interface ClassListInfoProps {
  isLoading: boolean;
  numClasses: number;
}

const NUMBER_OF_CLASSES = 5;
const UPCOMING_CLASSES_MAX_LIMIT = 10;

export const LoggedInHomeMyClassesList = () => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\LoggedInHomeMyClassesList"
  );
  const { currentUser } = useSession();
  const isMobile = useIsMobile();
  const navigate = useNavigation();

  const { activeLearner } = useActiveLearner({
    onActiveLearnerChanged() {
      setLimitUpcomingClasses(NUMBER_OF_CLASSES);
      setSelectedTab(ClassListTab.Upcoming);
    }
  });

  const [selectedTab, setSelectedTab] = React.useState<ClassListTab>(
    ClassListTab.Upcoming
  );

  const {
    completedClasses,
    completedClassesLoading,
    completedClassesError,
    completedClassesTotalCount
  } = useRecentlyCompletedClassList({
    filterByLearnerUid: activeLearner?.uid,
    limit: NUMBER_OF_CLASSES
  });

  const [limitUpcomingClasses, setLimitUpcomingClasses] =
    React.useState(NUMBER_OF_CLASSES);
  const {
    error: upcomingEnrollmentsError,
    loading: upcomingEnrollmentsLoading,
    renderedEnrollments: upcomingEnrollments,
    totalCount: upcomingEnrollmentsTotalCount
  } = useParentUpcomingClassesQuery({
    limitUpcomingClasses,
    filterByLearnerUid: activeLearner?.uid,
    includeNoMeetingsClasses: false
  });

  const handleChangeLimitUpcomingClasses = () => {
    setLimitUpcomingClasses(UPCOMING_CLASSES_MAX_LIMIT);
  };

  const hasMoreUpcomingClasses =
    !upcomingEnrollmentsLoading &&
    upcomingEnrollmentsTotalCount > upcomingEnrollments.length;
  const hasMoreCompletedClasses =
    !completedClassesLoading &&
    completedClassesTotalCount > completedClasses.length;

  const classTabs = [
    {
      label: activeLearner
        ? t("Upcoming classes for {{learnerName}}", {
            learnerName: activeLearner.name
          })
        : t("Upcoming classes"),
      value: ClassListTab.Upcoming
    },
    ...(completedClasses.length > 0 && !completedClassesLoading
      ? [
          {
            label: activeLearner
              ? t("Completed classes for {{learnerName}}", {
                  learnerName: activeLearner.name
                })
              : t("Completed classes"),
            value: ClassListTab.Completed
          }
        ]
      : [])
  ];

  const learnerCount = currentUser?.children?.length ?? 0;
  const upcomingEmptyTitle =
    learnerCount === 0
      ? t(
          "Add a learner and start discovering classes for every learning need!"
        )
      : t("No classes scheduled yet. Let’s find the next class.");

  if (isMobile) {
    return (
      <Box sx={{ marginTop: "2.4rem", marginBottom: "3.2rem" }}>
        <Typography
          variant="h4"
          sx={{ textAlign: "center", marginBottom: "1.6rem" }}
        >
          {classTabs[0]?.label}
        </Typography>
        <NextMeetingsOfParent />
        <ClassList
          enrollments={upcomingEnrollments}
          error={upcomingEnrollmentsError}
          loading={upcomingEnrollmentsLoading}
          emptyStateComponent={<EmptyListView title={upcomingEmptyTitle} />}
        />
        {hasMoreUpcomingClasses && (
          <ViewMoreButton onClick={handleChangeLimitUpcomingClasses} />
        )}
        {completedClasses.length > 0 && !completedClassesLoading && (
          <>
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                marginTop: "2.4rem",
                marginBottom: "1.6rem"
              }}
            >
              {classTabs[1]?.label}
            </Typography>
            <ClassList
              enrollments={completedClasses}
              error={completedClassesError}
              loading={completedClassesLoading}
              completed
            />
            {hasMoreCompletedClasses && (
              <ViewMoreButton onClick={() => navigate(transcriptsPath())} />
            )}
          </>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ marginTop: "2.4rem", marginBottom: "4.8rem" }}>
      <ClassListTabs
        tabs={classTabs}
        selectedTab={selectedTab}
        onChange={(value: string) => setSelectedTab(value as ClassListTab)}
        sx={{ marginBottom: "2.4rem" }}
      />
      <NextMeetingsOfParent />
      {selectedTab === ClassListTab.Upcoming && (
        <>
          <ClassList
            enrollments={upcomingEnrollments}
            error={upcomingEnrollmentsError}
            loading={upcomingEnrollmentsLoading}
            emptyStateComponent={<EmptyListView title={upcomingEmptyTitle} />}
          />
          {hasMoreUpcomingClasses && (
            <ViewMoreButton onClick={handleChangeLimitUpcomingClasses} />
          )}
        </>
      )}
      {selectedTab === ClassListTab.Completed && (
        <>
          <ClassList
            enrollments={completedClasses}
            error={completedClassesError}
            loading={completedClassesLoading}
            completed
          />
          {hasMoreCompletedClasses && (
            <ViewMoreButton onClick={() => navigate(transcriptsPath())} />
          )}
        </>
      )}
    </Box>
  );
};

const ViewMoreButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation(
    "client\\components\\loggedInHome\\LoggedInHomeMyClassesList"
  );

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Button
        variant="text"
        textColor="primary"
        sx={{ marginTop: "2rem" }}
        onClick={onClick}
      >
        {t("View more")}
      </Button>
    </Box>
  );
};
